import {
  PlatformController,
  MetaData,
  ObsidianActionHooks,
  PlatformState,
} from '../types';
import {Platform} from '../../../types';
import {Queue} from '../../Queue';

// Since MusicKitProvider already exists, initialize isn't needed in obsidian for musicKit.
// Later we can replace MusicKitProvider w/ the initialize methods of obsidian.
export default class AppleMusicController implements PlatformController {
  // Since MusicKit is initialized outside of obsidian, can't assign mk to musickit.getInstance() here
  // since its unauthorized at this stage and will crash. After refactor above ^ will change type from any to
  // MusicKit
  private queue: Queue;
  private upNext: Queue;
  private mk: any;
  private initialized: boolean;
  private actions: ObsidianActionHooks;
  private waitingForSongEnd = true;
  private waitingForPlayConfirmation = false;
  private waitingForPauseConfirmation = false;
  private waitingForSeekConfirmation = -1; // -1 is not waiting for seek, else store expected new position

  constructor(actions: ObsidianActionHooks, queue: Queue, upNext: Queue) {
    this.queue = queue;
    this.upNext = upNext;
    this.mk = window.MusicKit;
    this.initialized = false;
    this.actions = actions;
  }

  private handleSongFinished() {
    this.actions.onPause();
    console.log(this.queue, this.upNext);
    if (this.waitingForSongEnd && this.queue.queue.length > 0) {
      this.waitingForSongEnd = false;
      this.play(this.queue.queue[0].id);
      this.queue.setQueue(this.queue.queue.slice(1));
      this.actions.onSongFinished();
    } else if (this.waitingForSongEnd && this.upNext.queue.length > 0) {
      this.waitingForSongEnd = false;
      this.play(this.upNext.queue[0].id);
      this.upNext.setQueue(this.upNext.queue.slice(1));
      this.actions.onSongFinished();
    } else {
      this.waitingForSongEnd && this.actions.onSongFinished(true); // true param implies queue empty
    }
  }

  // Logic for playing the next song in queue when song ends is fragile
  // The action hooks passed can not reference context values (they will be stale), so I gave
  // the obsidian class the queue. This could be solved by completely replacing
  // the obsidian classes with hooks.
  private attachPlayerEvents(player: MusicKit.Player) {
    player.addEventListener('playbackProgressDidChange', () => {
      if (this.waitingForSeekConfirmation >= 0) {
        this.actions.onSeekTo(this.waitingForSeekConfirmation);
        this.waitingForSeekConfirmation = -1;
      }
    });
    player.addEventListener('metadataDidChange', () => {
      // TODO: better typings for mk.player.nowPlayingItem, and fallabacks when
      // albumImage, artist, etc. are null
      const currentSong = this.mk.player?.nowPlayingItem;
      currentSong &&
        this.actions.onMetaDataChange({
          name: currentSong.attributes.name,
          duration: currentSong.attributes.durationInMillis,
          artist: currentSong.attributes.artistName,
          albumImage: currentSong.artwork?.url || '',
        });
    });
    player.addEventListener('playbackStateDidChange', () => {
      const mkPlaying = this.mk.player.isPlaying;
      // This event listener is a more reliable indicator for when a song actually
      // starts/stops playing that the mk play() and pause() promises resolving.
      if (this.waitingForPlayConfirmation && mkPlaying) {
        this.waitingForPlayConfirmation = false;
        this.actions.onPlay();
      }
      if (this.waitingForPauseConfirmation && !mkPlaying) {
        this.waitingForPauseConfirmation = false;
        this.actions.onPause();
      }
      if (!mkPlaying && this.mk.player.currentPlaybackTimeRemaining <= 0) {
        this.handleSongFinished();
      }
      this.actions.onBufferChange(this.mk.player.currentBufferedProgress);
    });
    player.addEventListener('playbackTimeDidChange', () => {
      this.actions.onBufferChange(this.mk.player.currentBufferedProgress);
    });
  }

  async play(id: string) {
    if (!this.mk.setQueue) return;
    // TODO: If you try to play the currently playing song, should it do nothing
    // or restart the song? current behaviour is doing nothing
    if (
      id === this.mk.player?.nowPlayingItem?.container?.id ||
      id === this.mk.player?.nowPlayingItem?.id
    ) {
      return;
    }
    if (this.mk.player.isPlaying) {
      this.mk.player.stop();
    }
    this.actions.onNewSong();
    await this.mk.setQueue({song: id});
    this.mk.player.play();
    this.waitingForSongEnd = true;
    this.waitingForPlayConfirmation = true;
    // Now this.actions.onPlay() is called once event playbackStateDidChange reports it
  }

  async pause() {
    this.mk.player.pause();
    this.waitingForPauseConfirmation = true;
    // this.actions.onPause() called by playbackStateDidChange listener
  }

  //todo: deprecate resume
  resume() {}

  togglePlay() {
    if (this.mk.player.isPlaying) {
      this.mk.player.pause().then(() => {
        this.actions.onPause();
      });
    } else {
      this.mk.player.play().then(() => {
        this.actions.onPlay();
      });
    }
  }

  // TODO: investigate buggy song: bitches talkin' - frank ocean, when seeking:
  // "musickit.js:1 Uncaught (in promise) DOMException: Failed to read the 'buffered' property from 'SourceBuffer':
  // This SourceBuffer has been removed from the parent media source.""
  // UPDATE: The bug happens when playing any icloud library song after any apple music song
  // the bug exists in the official apple music web player itself.

  async seek(position: number, cb?: () => void) {
    const handleSeek = async () => {
      this.actions.onStopTimer();
      this.waitingForSeekConfirmation = position;
      await this.mk.player.seekToTime(position / 1000);
      cb && cb();
      // this.actions.onSeekTo(pos) will be called once `playbackProgressDidChange` listener reports
      // this.actions.onSeekTo(position);
    };
    if (this.mk.player.isPlaying) {
      handleSeek();
    } else {
      this.mk.player.play().then(() => {
        this.actions.onPlay();
        handleSeek();
      });
    }
  }

  async stop() {
    this.mk.player.stop();
  }

  //todo
  nextTrack() {}

  //todo
  previousTrack() {}

  //todo
  setVolume(value: number) {
    this.mk.player.volume = value;
    this.actions.onSetVolume(value);
  }

  //todo
  isReady() {
    return true;
  }

  //todo
  initialize(clientId: string) {
    this.mk = window.MusicKit.getInstance();
    this.attachPlayerEvents(this.mk);
    this.initialized = true;
  }

  //todo
  getTrackMetaData(state: any): MetaData | null {
    return {};
  }

  //todo: update linked state
  getState(): PlatformState {
    return {
      platform: Platform.AppleMusic,
      linked: true,
      connected: this.initialized,
    };
  }
}
